import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../../shared/utils/api';
import LeanDrillStats from '../components/LeanDrillStats';
import LeanDrillSession from '../components/LeanDrillSession';
import LeanTopicPicker from '../components/LeanTopicPicker';

const MODULES = [
  { value: 'both', label: 'Both' },
  { value: 'FLK1', label: 'FLK1' },
  { value: 'FLK2', label: 'FLK2' },
];

const MODES = [
  { value: 'weakest', label: 'Weakest topics', desc: 'Focus on your lowest-scoring areas' },
  { value: 'topic', label: 'Specific topic', desc: 'Choose a subject and study unit' },
  { value: 'random', label: 'Random', desc: 'Drills from across all topics' },
  { value: 'incorrect', label: 'Review incorrect', desc: 'Retry drills you got wrong' },
];

const PRESETS = [5, 10, 20];
const CUSTOM_DEFAULT = 20;
const CUSTOM_MAX = 180;

// Set to true when MCQ tier launches to re-enable the explanation block
const SHOW_DRILLS_VS_MCQS = false;

const VALID_MODES = new Set(MODES.map((m) => m.value));

export default function PracticePage() {
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [module, setModule] = useState('both');
  const [mode, setMode] = useState(() => {
    const params = new URLSearchParams(location.search);
    const param = params.get('mode');
    // 'specific' is an alias for 'topic' (used by Performance page deep links)
    const resolved = param === 'specific' ? 'topic' : param;
    const hasTopicIds = !!params.get('topic_ids');
    if (hasTopicIds && (!resolved || !VALID_MODES.has(resolved))) return 'topic';
    return resolved && VALID_MODES.has(resolved) ? resolved : 'weakest';
  });
  const [count, setCount] = useState(10);
  const [isCustom, setIsCustom] = useState(false);
  const [customInput, setCustomInput] = useState(String(CUSTOM_DEFAULT));
  const [sessionConfig, setSessionConfig] = useState(null);
  const [topicCounts, setTopicCounts] = useState([]);
  const [topicCountsLoading, setTopicCountsLoading] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState(() => {
    const raw = new URLSearchParams(location.search).get('topic_ids');
    if (!raw) return new Set();
    const ids = raw.split(',').map(Number).filter(Boolean);
    return new Set(ids);
  });

  const loadStats = useCallback(() => {
    api('/api/questions/stats?question_type=drill')
      .then((data) => setStats(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (mode !== 'topic' || topicCounts.length > 0) return;
    setTopicCountsLoading(true);
    api('/api/questions/topic-counts?question_type=drill')
      .then((d) => {
        setTopicCounts(d.topics || []);
        setTopicCountsLoading(false);
      })
      .catch(() => setTopicCountsLoading(false));
  }, [mode, topicCounts.length]);

  const handleToggleGroup = (l3Ids, shouldSelect) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      for (const id of l3Ids) {
        if (shouldSelect) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const handleCustomChange = (e) => {
    const raw = e.target.value;
    setCustomInput(raw);
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 1) setCount(Math.min(CUSTOM_MAX, n));
  };

  const handleCustomBlur = () => {
    const n = parseInt(customInput, 10);
    const clamped = isNaN(n) ? CUSTOM_DEFAULT : Math.min(CUSTOM_MAX, Math.max(1, n));
    setCustomInput(String(clamped));
    setCount(clamped);
  };

  const selectedL2Count = useMemo(() => {
    if (selectedTopicIds.size === 0 || topicCounts.length === 0) return 0;
    const l2s = new Set();
    for (const t of topicCounts) {
      if (selectedTopicIds.has(t.topic_id)) {
        l2s.add(t.general_topic || t.subject_area || 'General');
      }
    }
    return l2s.size;
  }, [selectedTopicIds, topicCounts]);

  const canStart = mode !== 'topic' || selectedTopicIds.size > 0;

  const handleStart = () => {
    setSessionConfig({
      module,
      mode,
      count,
      topicIds: Array.from(selectedTopicIds),
    });
  };

  const handleExit = () => {
    setSessionConfig(null);
    loadStats();
  };

  // ── Active session ──────────────────────────────────────────────────────────
  if (sessionConfig) {
    return (
      <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-4">
        <LeanDrillSession config={sessionConfig} onExit={handleExit} />
      </div>
    );
  }

  // ── Topic picker ────────────────────────────────────────────────────────────
  if (mode === 'topic') {
    return (
      <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-6">
        <h1 className="text-2xl font-bold text-text-primary mb-sp-5">Practice</h1>

        <div className="bg-bg-card rounded-r-xl border border-border-soft">
          <div className="px-sp-6 py-sp-5">
            <p className="text-[15px] font-semibold text-text-primary mb-sp-4">
              Choose a subject and study unit
            </p>
            {topicCountsLoading ? (
              <div className="py-sp-8 text-center text-sm text-text-muted">Loading topics</div>
            ) : (
              <LeanTopicPicker
                topics={topicCounts}
                selectedIds={selectedTopicIds}
                onToggleGroup={handleToggleGroup}
              />
            )}
            <div className="flex items-center justify-between mt-sp-4">
              <button
                type="button"
                onClick={() => {
                  setMode('weakest');
                  setSelectedTopicIds(new Set());
                }}
                className="px-sp-3 py-sp-2 text-sm font-medium text-text-secondary border border-border-soft rounded-r-md hover:bg-bg-page transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleStart}
                disabled={!canStart}
                className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {selectedL2Count > 0
                  ? `Start session (${selectedL2Count} study unit${selectedL2Count === 1 ? '' : 's'})`
                  : 'Start session'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Setup ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-6">
      <h1 className="text-2xl font-bold text-text-primary mb-sp-5">Practice</h1>

      <LeanDrillStats stats={stats} />

      <div className="bg-bg-card rounded-r-xl border border-border-soft">
        <div className="px-sp-6 py-sp-5">
          <p className="text-[15px] font-semibold text-text-primary mb-sp-4">Start a session</p>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">Module</p>
          <div className="inline-flex border border-border-soft rounded-r-md overflow-hidden mb-sp-5">
            {MODULES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setModule(m.value)}
                className={`px-sp-5 py-sp-2 text-sm font-medium transition-colors ${
                  module === m.value
                    ? 'bg-accent text-white'
                    : 'bg-transparent text-text-secondary hover:bg-bg-page'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">What to practise</p>
          <div className="grid grid-cols-2 gap-sp-2 mb-sp-5">
            {MODES.map((m) => {
              const active = mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMode(m.value)}
                  className={`text-left rounded-r-lg px-sp-4 py-sp-3 transition-colors ${
                    active
                      ? 'border-2 border-accent bg-blue-lightest'
                      : 'border border-border-soft hover:border-border-medium'
                  }`}
                >
                  <h3
                    className={`text-sm font-semibold mb-sp-1 ${
                      active ? 'text-accent' : 'text-text-primary'
                    }`}
                  >
                    {m.label}
                  </h3>
                  <p className="text-xs text-text-muted">{m.desc}</p>
                </button>
              );
            })}
          </div>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">Number of drills</p>
          <div className="flex items-center gap-sp-2 mb-sp-2">
            {PRESETS.map((n) => {
              const active = !isCustom && count === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    setIsCustom(false);
                    setCount(n);
                  }}
                  className={`w-[44px] h-[36px] rounded-[6px] text-sm font-medium transition-colors ${
                    active
                      ? 'border-2 border-accent bg-blue-lightest text-accent'
                      : 'border border-border-soft bg-bg-card text-text-muted hover:border-border-medium'
                  }`}
                >
                  {n}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setIsCustom(true);
                const n = parseInt(customInput, 10);
                const clamped = isNaN(n) ? CUSTOM_DEFAULT : Math.min(CUSTOM_MAX, Math.max(1, n));
                setCount(clamped);
              }}
              className={`h-[36px] px-sp-3 rounded-[6px] text-sm font-medium transition-colors ${
                isCustom
                  ? 'border-2 border-accent bg-blue-lightest text-accent'
                  : 'border border-border-soft bg-bg-card text-text-muted hover:border-border-medium'
              }`}
            >
              Custom
            </button>
            {isCustom && (
              <input
                type="number"
                min={1}
                max={CUSTOM_MAX}
                value={customInput}
                onChange={handleCustomChange}
                onBlur={handleCustomBlur}
                className="w-[72px] h-[36px] border border-border-soft rounded-[6px] px-sp-2 text-sm font-medium text-text-primary bg-bg-card outline-none focus:border-accent text-center"
              />
            )}
          </div>
          <p className="text-xs text-text-muted mb-sp-5">
            {isCustom ? `Max ${CUSTOM_MAX} drills per session` : 'Immediate feedback after each drill'}
          </p>

          <button
            type="button"
            onClick={handleStart}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
          >
            Start session
          </button>
        </div>
      </div>

      {SHOW_DRILLS_VS_MCQS && (
        <div className="mt-sp-4 px-sp-5 py-sp-4 rounded-r-lg bg-[rgba(59,130,246,0.04)] border border-[rgba(59,130,246,0.12)] text-xs text-text-secondary leading-relaxed">
          <strong className="text-text-primary">Drills vs MCQs:</strong> Drills test your recall of
          specific legal rules without a scenario. They are designed for learning and embedding
          knowledge. MCQs test your ability to apply rules to a fact pattern, closer to exam
          conditions.
        </div>
      )}
    </div>
  );
}
